import { Link } from '@tanstack/react-router';
import { CircleArrowLeft } from 'lucide-react';
import { useState } from 'react';

import { useLateFeesSettings } from '~features/payment/hooks/use-late-fees-settings';
import { ExclusionCard } from '~features/payment/ui/exclusion-card';
import { ExclusionModal } from '~features/payment/ui/exclusion-modal';

import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { Separator } from '~shared/shadcn/ui/separator';

export type ExclusionType = 'sessions' | 'classes' | 'events' | 'parents';

export const LateFees = () => {
  const {
    feeType,
    setFeeType,
    feeAmount,
    setFeeAmount,
    interestRate,
    setInterestRate,
    gracePeriod,
    setGracePeriod,
    exclusions,
    handleSave,
  } = useLateFeesSettings();

  const [activeModal, setActiveModal] = useState<ExclusionType | null>(null);
  const [isActive, setIsActive] = useState(false);

  const openModal = (type: ExclusionType) => {
    setActiveModal(type);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  return (
    <div>
      <div className='flex items-center gap-6'>
        <Link to='/dashboard' className='flex w-max items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Late Fees Settings</h2>
        </Link>
        <Badge>Coming soon</Badge>
      </div>

      <div className='mt-8 border-t' />

      <div className='container relative mt-6 max-w-5xl'>
        <Card>
          <CardHeader className='flex flex-col gap-3 sm:flex-row sm:justify-between'>
            <div className='w-max'>
              <CardTitle className='text-2xl font-bold'>
                Late Fees Settings
              </CardTitle>
              <CardDescription>
                Configure how late fees are calculated and applied to enrolments
              </CardDescription>
            </div>
            <div>
              {/* Active Toggle */}
              <div className='inline-flex items-center justify-between rounded-lg border bg-white p-3 shadow-sm'>
                <div className='flex items-center space-x-3'>
                  <div className='flex flex-col'>
                    <span className='text-sm text-gray-500'>
                      {isActive ? 'Late Fees active' : 'Late Fees inactive'}
                    </span>
                  </div>
                </div>
                <div className='ml-8'>
                  <button
                    type='button'
                    onClick={() => {
                      setIsActive(!isActive);
                    }}
                    className={`relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                      isActive ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block size-7 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        isActive ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className='space-y-6'>
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='fee-type'>Late Fee Type</Label>
                <Select value={feeType} onValueChange={setFeeType}>
                  <SelectTrigger id='fee-type'>
                    <SelectValue placeholder='Select fee type' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='fixed'>Fixed Amount</SelectItem>
                    <SelectItem value='percentage'>Percentage</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {feeType === 'fixed' ? (
                <div className='space-y-2'>
                  <Label htmlFor='fee-amount'>Late Fee Amount ($)</Label>
                  <Input
                    id='fee-amount'
                    type='number'
                    value={feeAmount}
                    onChange={(e) => {
                      setFeeAmount(e.target.value);
                    }}
                    placeholder='25.00'
                  />
                </div>
              ) : (
                <div className='space-y-2'>
                  <Label htmlFor='interest-rate'>Interest Rate (%)</Label>
                  <Input
                    id='interest-rate'
                    type='number'
                    value={interestRate}
                    onChange={(e) => {
                      setInterestRate(e.target.value);
                    }}
                    placeholder='5.00'
                  />
                </div>
              )}

              <div className='space-y-2'>
                <Label htmlFor='grace-period'>Grace Period (days)</Label>
                <Input
                  id='grace-period'
                  type='number'
                  value={gracePeriod}
                  onChange={(e) => {
                    setGracePeriod(e.target.value);
                  }}
                  placeholder='7'
                />
              </div>
            </div>

            <Separator className='my-4' />

            <div className='space-y-4'>
              <h3 className='text-lg font-medium'>Excluded from Late Fees</h3>
              <p className='text-sm text-muted-foreground'>
                Select items that should be excluded from late fee calculations
              </p>

              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <ExclusionCard
                  title='Sessions'
                  count={exclusions.sessions.length}
                  onManage={() => {
                    openModal('sessions');
                  }}
                />
                <ExclusionCard
                  title='Classes'
                  count={exclusions.classes.length}
                  onManage={() => {
                    openModal('classes');
                  }}
                />
                <ExclusionCard
                  title='Events'
                  count={exclusions.events.length}
                  onManage={() => {
                    openModal('events');
                  }}
                />
                <ExclusionCard
                  title='Parents'
                  count={exclusions.parents.length}
                  onManage={() => {
                    openModal('parents');
                  }}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className='flex justify-end space-x-2 border-t p-6'>
            <Button variant='outline'>Cancel</Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </CardFooter>
        </Card>

        <ExclusionModal type={activeModal} onClose={closeModal} />
        <div className='absolute inset-0 z-10 size-full rounded-lg bg-black/20' />
      </div>
    </div>
  );
};
